import { ReactNode, useCallback, useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { Bounce, toast } from "react-toastify";
import { persistor } from "services/apolloClient";
import * as serviceWorkerRegistration from "../serviceWorkerRegistration";
import { Button } from "@/components/Button/Button";

interface ServiceWorkerRegistrationProviderProps {
  children: ReactNode;
}

export const ServiceWorkerRegistrationProvider = ({
  children,
}: ServiceWorkerRegistrationProviderProps) => {
  const onServiceWorkerUpdate = useCallback(
    (registration: ServiceWorkerRegistration) => {
      toast(<RefreshToast registration={registration} />, {
        icon: false,
        position: "bottom-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        style: {
          backgroundColor: "transparent",
          boxShadow: "none",
          width: "600px",
        },
        transition: Bounce,
      });
    },
    []
  );

  useEffect(() => {
    serviceWorkerRegistration.register({
      onUpdate: onServiceWorkerUpdate,
    });
  }, [onServiceWorkerUpdate]);

  return <>{children}</>;
};

interface RefreshToastProps {
  registration: ServiceWorkerRegistration;
}

const cachesToClearOnUpdate = [
  "keycloak",
  "images",
  "translations",
  "custom-html",
];

const RefreshToast = ({ registration }: RefreshToastProps) => {
  const [loading, setLoading] = useState<boolean>(false);

  const onPageRefresh = async () => {
    toast.dismiss();
    setLoading(true);
    registration.waiting?.postMessage({ type: "SKIP_WAITING" });
    // clear caches
    (await caches.keys()).forEach((cacheName) => {
      if (cachesToClearOnUpdate.includes(cacheName)) caches.delete(cacheName);
    });
    // clear apollo's local storage cache
    await persistor.purge();
    window.location.reload();
    setLoading(false);
  };

  return (
    <div className="relative overflow-hidden flex items-center gap-4 p-4 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 dark:from-blue-600 dark:via-indigo-600 dark:to-purple-700 rounded-xl shadow-2xl">
      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />

      {/* Glow effect */}
      <div className="absolute inset-0 opacity-50 blur-xl bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-500 -z-10" />

      <div className="relative flex-shrink-0">
        <div
          className={`size-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg transition-all duration-300 ${
            loading ? "animate-spin" : "animate-pulse"
          }`}
        >
          {loading ? (
            <RefreshCw className="size-6 text-white animate-spin" />
          ) : (
            <Sparkles className="size-6 text-white" />
          )}
        </div>
      </div>

      <div className="relative flex-1 min-w-0">
        <div className="text-base font-bold text-white mb-1 drop-shadow-sm">
          ✨ New Version Available!
        </div>
        <div className="text-sm text-white/90 drop-shadow-sm">
          {loading
            ? "Updating application..."
            : "Click refresh to get the latest features"}
        </div>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={onPageRefresh}
        loading={loading}
        disabled={loading}
        className="relative flex-shrink-0 bg-white/20 hover:bg-white/30 backdrop-blur-sm border-white/30 text-white font-semibold shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {!loading && <RefreshCw className="size-4" />}
        {loading ? "Updating..." : "Refresh"}
      </Button>
    </div>
  );
};
